import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Undefinable } from "@core/domain/types/undefinable.type.ts";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { Station } from "@station/domain/models/station.model";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const SpatialLevelBaseSchema = z.object({
    id: zIdSchema,
    name: z.string().trim(),
    toLabel: z.string().trim(),
    stations: z.array(
        z.custom<Station>((val) => val instanceof Station, {
            error: "Invalid Station instance",
        }),
    ),
});

const SpatialLevelSchema = SpatialLevelBaseSchema.extend({
    /**
     * Recursive type, zod needs manual type definition due typescript limitations
     * @see https://github.com/colinhacks/zod#recursive-types
     */
    children: z
        .array(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            z.custom<SpatialLevel>((val) => val instanceof SpatialLevel, {
                error: "Invalid SpatialLevel instance",
            }),
        )
        .optional(),
}).brand<"SpatialLevel">();

export type SpatialLevelInputProps = z.input<typeof SpatialLevelSchema>;
type SpatialLevelProps = z.output<typeof SpatialLevelSchema>;

export class SpatialLevel {
    private constructor(private readonly props: Readonly<SpatialLevelProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get toLabel(): string {
        return this.props.toLabel;
    }

    get stations(): readonly Readonly<Station>[] {
        return this.props.stations;
    }

    get children(): Undefinable<readonly Readonly<SpatialLevel>[]> {
        return this.props.children;
    }

    static empty(): SpatialLevel {
        return SpatialLevel.create({
            id: 0,
            name: "",
            toLabel: "",
            stations: [],
            children: [],
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<SpatialLevelInputProps>>,
    ): Result<SpatialLevelProps, z.ZodError<SpatialLevelProps>> {
        const result = SpatialLevelSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<SpatialLevelInputProps>>,
    ): Result<SpatialLevelProps, PartialValid<typeof SpatialLevel>> {
        const result = partialSafeParse(SpatialLevelSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<SpatialLevelInputProps>,
    ): Result<SpatialLevel, z.ZodError<SpatialLevelProps>> {
        return SpatialLevel.validate(props).map(
            (validatedProps) => new SpatialLevel(validatedProps),
        );
    }

    static createWithRecovery(
        props: SpatialLevelInputProps,
    ): Result<SpatialLevel, ModelError<typeof SpatialLevel>> {
        return SpatialLevel.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const recoveryProps: Unbrand<SpatialLevelProps> = {
                    id: error.validProps.id,
                    name: "",
                    toLabel: "",
                    stations: [],
                    children: [],
                };

                merge(recoveryProps, error.validProps);

                return new RecuperableModelError<typeof SpatialLevel>(
                    new SpatialLevel(recoveryProps as SpatialLevelProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new SpatialLevel(validatedProps));
    }

    toCreateProps(): SpatialLevelInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            toLabel: this.props.toLabel,
            stations: this.props.stations,
            children: this.props.children,
        };
    }
}

const SpatialLevelsSchema = z
    .object({
        spatialLevels: z.array(
            z.custom<SpatialLevel>((val) => val instanceof SpatialLevel, {
                error: "Invalid SpatialLevel instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"SpatialLevels">();

export type SpatialLevelsInputProps = z.input<typeof SpatialLevelsSchema>;
export type SpatialLevelsProps = z.output<typeof SpatialLevelsSchema>;

export class SpatialLevels {
    // Stores spatial levels and stations in a Map for fast access by ID
    private readonly spatialLevelsMap = new Map<
        number,
        Readonly<SpatialLevel>
    >();
    readonly stationsMap = new Map<number, Readonly<Station>>();

    private constructor(private readonly props: Readonly<SpatialLevelsProps>) {
        this.buildMaps();
    }

    get spatialLevels(): readonly Readonly<SpatialLevel>[] {
        return this.props.spatialLevels;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): SpatialLevels {
        return SpatialLevels.create({
            spatialLevels: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<SpatialLevelsInputProps>>,
    ): Result<SpatialLevelsProps, z.ZodError<SpatialLevelsProps>> {
        const result = SpatialLevelsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<SpatialLevelsInputProps>,
    ): Result<SpatialLevels, z.ZodError<SpatialLevelsProps>> {
        return SpatialLevels.validate(props).map(
            (validatedProps) => new SpatialLevels(validatedProps),
        );
    }

    // Builds a Map of spatial levels and stations for quick access
    private buildMaps(): void {
        const traverseLevels = (level: Readonly<SpatialLevel>): void => {
            this.spatialLevelsMap.set(level.id.valueOrThrow, level);

            level.stations.forEach((station) => {
                this.stationsMap.set(station.id.valueOrThrow, station);
            });

            level.children?.forEach(traverseLevels);
        };

        this.spatialLevels.forEach(traverseLevels);
    }

    // Retrieves a spatial level by ID
    getById(levelId: number): Readonly<SpatialLevel> | undefined {
        return this.spatialLevelsMap.get(levelId);
    }

    // Retrieves a stations by ID
    getStationById(stationId: number): Readonly<Station> | undefined {
        return this.stationsMap.get(stationId);
    }

    withStationRefreshed(refreshed: Readonly<Station>): SpatialLevels {
        const replaceInLevel = (
            level: Readonly<SpatialLevel>,
        ): SpatialLevel => {
            const nextStations = level.stations.map((station) =>
                station.id.valueOrThrow === refreshed.id.valueOrThrow
                    ? refreshed
                    : station,
            );
            const nextChildren = level.children?.map(replaceInLevel);

            return SpatialLevel.create({
                ...level.toCreateProps(),
                stations: nextStations,
                children: nextChildren,
            })._unsafeUnwrap();
        };

        return SpatialLevels.create({
            spatialLevels: this.spatialLevels.map(replaceInLevel),
            count: this.count,
        })._unsafeUnwrap();
    }
}

export const SpatialLevelUpdateSchema = z.object({
    id: z.number().positive(),
    name: z.string().trim().min(1),
});
export type SpatialLevelUpdate = z.output<typeof SpatialLevelUpdateSchema>;
